import { Component, inject, signal, TemplateRef, viewChild } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { ToastrService } from 'ngx-toastr'
import { DirectoryService } from 'src/app/core/services/company/directory.service'
import { CardComponent } from 'src/app/shared/components/card/card.component'
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { InputTextareaComponent } from 'src/app/shared/components/form-elements/input-textarea/input-textarea.component'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { PostcodeLookupComponent } from 'src/app/shared/components/form-elements/postcode-lookup/postcode-lookup.component'
import { PageActionBarComponent } from 'src/app/shared/components/page-action-bar/page-action-bar.component'
import { PageTitleComponent } from 'src/app/shared/components/page-title/page-title.component'
import { PaginationDetailComponent } from 'src/app/shared/components/pagination-detail/pagination-detail.component'
import { TableModule } from 'src/app/shared/components/table/table.module'
import { DirectoryFilter } from 'src/app/shared/interfaces/company/directory/directory-filter.interface'
import { DirectoryItem } from 'src/app/shared/interfaces/company/directory/directory-item.interface'
import { PostcodeAddress } from 'src/app/shared/interfaces/postcode-lookup/postcode-address.interface'

@Component({
    selector: 'sf-directory',
    imports: [
        TableModule,
        ReactiveFormsModule,
        PageTitleComponent,
        PageActionBarComponent,
        ButtonComponent,
        CardComponent,
        PaginationDetailComponent,
        InputComponent,
        InputTextareaComponent,
        PostcodeLookupComponent,
    ],
    templateUrl: './directory.component.html',
    styleUrl: './directory.component.scss',
})
export class DirectoryComponent {
    #confirmationService = inject(ConfirmationDialogService)
    #directoryService = inject(DirectoryService)
    #fb = inject(FormBuilder)
    #modalService = inject(NgbModal)
    #toastr = inject(ToastrService)

    modal = viewChild<NgbModalRef>('directoryModal')

    #filter = signal<DirectoryFilter>({
        pageNumber: 1,
        pageSize: 20,
    })

    #directoryResource = rxResource({
        params: () => this.#filter(),
        stream: ({ params }) => this.#directoryService.getDirectoryPaged(params),
    })

    isLoading = this.#directoryResource.isLoading
    directory = this.#directoryResource.value

    form = this.#fb.group({
        name: this.#fb.nonNullable.control<string>('', [Validators.required, Validators.maxLength(255)]),
        addressLine1: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        addressLine2: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        addressLine3: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        addressLine4: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        locality: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        town: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        county: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        postcode: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        phone1: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        phone2: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        phone3: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
        emailAddress: this.#fb.nonNullable.control<string>('', [Validators.email, Validators.maxLength(255)]),
        notes: this.#fb.nonNullable.control<string>('', [Validators.maxLength(10_000)]),
    })

    selectedDirectory = signal<DirectoryItem | null>(null)

    onSelectedDirectory(directory: DirectoryItem) {
        this.selectedDirectory.set(directory)
        this.form.patchValue(directory)

        this.#modalService.open(this.modal(), { size: 'xl' })
    }

    onCreateNewDirectory() {
        this.selectedDirectory.set(null)
        this.form.reset()
        this.#modalService.open(this.modal(), { size: 'xl' })
    }

    onAddressSelected(address: PostcodeAddress) {
        this.form.patchValue({
            addressLine1: address.envelopeAddress.line1,
            addressLine2: address.envelopeAddress.line2,
            town: address.envelopeAddress.town,
            county: address.envelopeAddress.county,
            postcode: address.postcode,
        })
    }

    saveDirectory() {
        this.form.markAllAsTouched()
        this.form.markAsDirty()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        if (this.selectedDirectory()) {
            this.updateDirectory()
        } else {
            this.createDirectory()
        }
    }

    private updateDirectory() {
        if (!this.selectedDirectory()) {
            return
        }

        this.#directoryService
            .updateDirectoryItem(this.selectedDirectory()!.id, this.form.value as DirectoryItem)
            .subscribe({
                next: () => {
                    this.#modalService.dismissAll()
                    this.#directoryResource.reload()
                    this.#toastr.success('Directory item updated successfully')
                },
                error: (err) => {
                    this.#toastr.error(err.error.message, 'Error')
                },
            })
    }

    private createDirectory() {
        this.#directoryService.createDirectoryItem(this.form.value as DirectoryItem).subscribe({
            next: () => {
                this.#modalService.dismissAll()
                this.#directoryResource.reload()
                this.#toastr.success('Directory item created successfully')
            },
            error: (err) => {},
        })
    }

    onDeleteDirectory() {
        if (!this.selectedDirectory()) {
            return
        }
        this.#confirmationService
            .confirm('Are you sure?', 'Are you sure you want to delete this directory?')
            .then((result) => {
                if (!result) {
                    return
                }
                this.#directoryService.deleteDirectoryItem(this.selectedDirectory()!.id).subscribe({
                    next: () => {
                        this.#filter.update((prev) => ({ ...prev, pageNumber: 1 }))
                        this.#directoryResource.reload()
                        this.#modalService.dismissAll()
                        this.#toastr.success('Directory item deleted successfully')
                    },
                    error: (err) => {
                        console.log(err)
                        this.#toastr.error(err.error.message, 'Error')
                    },
                })
            })
    }

    onPageChanged(page: number): void {
        this.#filter.update((prev) => ({ ...prev, pageNumber: page }))
    }
}
